import React, { useEffect, useState } from "react";
import {
  useSubscriptionHistory,
  SubscriptionHistoryTable,
} from "../../components/SubscriptionHistory";
import SubscriptionEditModal from "../../components/SubscriptionHistory/SubscriptionEditModal";
import { FilterSection, ActiveFilterPills } from "../../components/UserProfiles";
import PaginationBar from "../../components/common/PaginationBar";
import { getAdminPanelRole } from "../../utils/adminPanelRole";
import adminService from "../../api/services/adminService";

const SUBSCRIPTION_PILL_CLASSES = {
  recordType: "bg-sky-100 text-sky-800",
  user_id: "bg-purple-100 text-purple-800",
  name: "bg-slate-100 text-slate-800",
  email: "bg-cyan-100 text-cyan-800",
  contactNo: "bg-orange-100 text-orange-800",
  date_from: "bg-blue-100 text-blue-800",
  date_to: "bg-green-100 text-green-800",
  subscription: "bg-indigo-100 text-indigo-800",
  source: "bg-amber-100 text-amber-800",
  record_state: "bg-emerald-100 text-emerald-800",
  currency: "bg-teal-100 text-teal-800",
  status: "bg-rose-100 text-rose-800",
  emailType: "bg-violet-100 text-violet-800",
};

function getSubscriptionPillClassName(key) {
  return SUBSCRIPTION_PILL_CLASSES[key] || "bg-gray-100 text-gray-800";
}

const Subscription = () => {
  const {
    historyFilters,
    handleFilterChange,
    clearFilters,
    removeFilter,
    getFilterLabel,
    getActiveFiltersCount,
    activeFilterEntries,
    primaryFields,
    subscriptionHistory,
    currentPage,
    setCurrentPage,
    totalPages,
    totalItems,
    manualStats,
    revenueCatStats,
    loadingHistory,
    historyError,
    limit,
    fetchSubscriptionHistory,
  } = useSubscriptionHistory({ enabled: true });

  const isDeveloper = getAdminPanelRole() === "developer";
  const canDelete = isDeveloper;
  const canEdit = isDeveloper;
  const [editItem, setEditItem] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);

  useEffect(() => {
    if (!isDeveloper) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await adminService.getSubscriptionPlans();
        if (!cancelled && Array.isArray(res?.plans)) setSubscriptionPlans(res.plans);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isDeveloper]);

  return (
    <div className="p-4 sm:p-6 w-full min-w-0 overflow-x-hidden bg-gray-50/60 min-h-[calc(100vh-4rem)]">
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="p-5 sm:p-6 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 tracking-tight">
                All Records
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                All subscription records from userSubscriptions. Use filters to narrow results.
              </p>
            </div>
            {getActiveFiltersCount() > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors"
              >
                Clear All Filters
              </button>
            )}
          </div>
        </div>
        <div className="p-5 sm:p-6">
          <ActiveFilterPills
            activeKeys={activeFilterEntries}
            filters={historyFilters}
            getLabel={getFilterLabel}
            onRemove={removeFilter}
            label="Active Filters:"
            getPillClassName={getSubscriptionPillClassName}
          />
          <FilterSection
            primaryFields={primaryFields}
            secondaryFields={[]}
            values={historyFilters}
            onChange={(e) => handleFilterChange(e.target.name, e.target.value)}
            onApply={() => setCurrentPage(1)}
            onClear={clearFilters}
            applyLabel="Apply"
            clearLabel="Clear All"
          />
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3">
            <span className="text-sm font-medium text-gray-700">Manual subscriptions:</span>
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
              {manualStats.active} active
            </span>
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
              {manualStats.expired} expired
            </span>
            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
              {manualStats.canceled} canceled
            </span>
          </div>
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3">
            <span className="text-sm font-medium text-gray-700">RevenueCat subscriptions:</span>
            <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
              {revenueCatStats.active} active
            </span>
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
              {revenueCatStats.expired} expired
            </span>
            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
              {revenueCatStats.canceled} canceled
            </span>
          </div>
          <SubscriptionHistoryTable
            items={subscriptionHistory}
            isLoading={loadingHistory}
            error={historyError}
            onRetry={() => fetchSubscriptionHistory(currentPage, historyFilters)}
            canDelete={canDelete}
            canEdit={canEdit}
            onEditRow={setEditItem}
          />
          <SubscriptionEditModal
            open={Boolean(editItem)}
            item={editItem}
            plans={subscriptionPlans}
            onClose={() => setEditItem(null)}
            onSaved={() => {
              setEditItem(null);
              fetchSubscriptionHistory(currentPage, historyFilters);
            }}
          />
          <PaginationBar
            page={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={limit}
            label="subscription records"
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default Subscription;
